import React, { useMemo, useState } from 'react';
import Drilldown from '../../components/Drilldown';
import { fmtNumber, useFetch } from '../../hooks';
import { Purpose, Alert, Card, Chip, Skeleton, Stat } from '../../ui/basics';
import { BarChart, LineChart } from '../../ui/charts';

const WINDOWS = [6, 12, 24, 36];
const monthName = (key, partial) => {
  const [y, m] = key.split('-').map(Number);
  return `${new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short', year: '2-digit' })}${partial ? ' (to date)' : ''}`;
};
const signed = (n) => (n > 0 ? `+${n}` : String(n));

export default function Headcount({ org, lead }) {
  const [months, setMonths] = useState(12);
  const [drill, setDrill] = useState(null);
  const { data, loading, error } = useFetch('analytics/headcount/', { organization: org, lead, months });
  const rows = useMemo(() => (data ? data.months.map((m) => ({ ...m, label: monthName(m.month, m.partial) })) : []), [data]);
  const s = data && data.summary;

  const open = (kind, m) => setDrill({
    title: `${kind === 'started' ? 'Started' : 'Ended'} in ${m.label}`,
    filters: kind === 'started' ? { joined_from: m.first_day, joined_to: m.last_day } : { ended_from: m.first_day, ended_to: m.last_day },
  });

  return (
    <div className="stack">
      <Purpose what="Shows whether the number of active placements is growing or shrinking, by counting how many started and ended each month." use="Compare the Started and Ended bars month by month; click a bar or number to see those placements." care="The current month is only counted up to today, so it looks smaller than a full month." />
      <Card title="Headcount flow" subtitle="Placements starting and ending each month, and how many are active at month end."
        actions={<div className="row" style={{ gap: 6 }}>{WINDOWS.map((n) => <Chip key={n} on={n === months} onClick={() => setMonths(n)}>{n} months</Chip>)}</div>}>
        {error && <Alert>{error}</Alert>}
        {!s ? <Skeleton h={70} /> : (
          <div className="grid cols-4">
            <Stat label="Active now" value={fmtNumber(s.headcount_now)} sub={`${signed(s.headcount_now - s.headcount_start)} over ${months} months`} />
            <Stat label="Started" value={fmtNumber(s.started)} sub={`about ${Math.round(s.started / months)} a month`} />
            <Stat label="Ended" value={fmtNumber(s.ended)} sub={`about ${Math.round(s.ended / months)} a month`} />
            <Stat label="Net change" value={signed(s.net)} sub={`best ${monthName(s.best_month.month)} (${signed(s.best_month.net)}), worst ${monthName(s.worst_month.month)} (${signed(s.worst_month.net)})`} />
          </div>
        )}
      </Card>

      <Card title="Started and ended" subtitle="Click a bar to see those placements">
        {loading && !data ? <Skeleton h={320} /> : <BarChart data={rows} keys={['started', 'ended']} indexBy="label" colors={['#12b76a', '#f04438']}
          rotate={months > 12 ? -45 : 0} onClick={(bar) => open(bar.id, bar.data)} />}
      </Card>

      <Card title="Active headcount at month end" subtitle="The current month is counted up to today.">
        {loading && !data ? <Skeleton h={260} /> : <LineChart height={260} data={[{ id: 'Active placements', data: rows.map((m) => ({ x: m.label, y: m.headcount })) }]} />}
      </Card>

      <Card flush title="Month by month">
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Month</th><th className="r">Started</th><th className="r">Ended</th><th className="r">Net</th><th className="r">Active at month end</th></tr></thead>
          <tbody>
            {[...rows].reverse().map((m) => (
              <tr key={m.month}>
                <td><b>{m.label}</b></td>
                <td className="r num"><button type="button" className="link" disabled={!m.started} onClick={() => open('started', m)}>{m.started}</button></td>
                <td className="r num"><button type="button" className="link" disabled={!m.ended} onClick={() => open('ended', m)}>{m.ended}</button></td>
                <td className="r num" style={{ color: m.net > 0 ? '#027a48' : m.net < 0 ? 'var(--danger)' : undefined, fontWeight: 600 }}>{signed(m.net)}</td>
                <td className="r num">{fmtNumber(m.headcount)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      {drill && <Drilldown title={drill.title} org={org} lead={lead} filters={drill.filters} onClose={() => setDrill(null)} />}
    </div>
  );
}
